import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";
import { ApiError, tenants } from "../lib/api";
import type { EncuestaBaseConfig, ResolvioOption } from "../lib/api";

export function PlatformEncuestaBasePage() {
  const [cfg, setCfg] = useState<EncuestaBaseConfig | null>(null);
  const [draft, setDraft] = useState<EncuestaBaseConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2800);
  };

  useEffect(() => {
    setLoading(true);
    tenants
      .getEncuestaBase()
      .then((c) => {
        setCfg(c);
        setDraft(c);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "No se pudo cargar"))
      .finally(() => setLoading(false));
  }, []);

  const update = <K extends keyof EncuestaBaseConfig>(
    key: K,
    val: EncuestaBaseConfig[K],
  ) => {
    if (!draft) return;
    setDraft({ ...draft, [key]: val });
  };

  const updateOption = (idx: number, patch: Partial<ResolvioOption>) => {
    if (!draft) return;
    setDraft({
      ...draft,
      resolvio_options: draft.resolvio_options.map((o, i) =>
        i === idx ? { ...o, ...patch } : o,
      ),
    });
  };

  const addOption = () => {
    if (!draft) return;
    if (draft.resolvio_options.length >= 8) return;
    setDraft({
      ...draft,
      resolvio_options: [
        ...draft.resolvio_options,
        {
          id: `opcion_${draft.resolvio_options.length + 1}`,
          label: "",
          desc: "",
          positiva: false,
        },
      ],
    });
  };

  const removeOption = (idx: number) => {
    if (!draft) return;
    if (draft.resolvio_options.length <= 2) return;
    setDraft({
      ...draft,
      resolvio_options: draft.resolvio_options.filter((_, i) => i !== idx),
    });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const c = await tenants.updateEncuestaBase(draft);
      setCfg(c);
      setDraft(c);
      flash("ok", "Configuración guardada");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  if (error || !draft || !cfg) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "No se pudo cargar"}</div>
          <p>
            <Link to="/platform/config">← Configuración</Link>
          </p>
        </section>
      </Shell>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(cfg);

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to="/platform/config">← Configuración</Link> · Encuesta base
        </div>
        <h1 style={{ fontSize: 28, marginTop: 6 }}>
          Preguntas base de la encuesta
        </h1>
        <p className="lede" style={{ maxWidth: 720 }}>
          Estas 3 preguntas se envían a TODAS las alcaldías tras cada respuesta
          a PQRSDF. Edítalas con cuidado: cambios aplican inmediatamente. Las
          métricas históricas usan los IDs de las opciones de "Resolvió" para
          calcular el % resolvió.
        </p>

        {/* Intro */}
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-inner">
            <div className="eyebrow">Texto introductorio</div>
            <p className="helper">
              Aparece como subtítulo arriba del formulario en la página pública.
            </p>
            <input
              type="text"
              className="input"
              value={draft.intro}
              maxLength={300}
              onChange={(e) => update("intro", e.target.value)}
            />
          </div>
        </div>

        {/* Pregunta 1: rating */}
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-inner">
            <div className="eyebrow">Pregunta 1 — Calificación (estrellas 1-5)</div>
            <div className="form-row" style={{ marginTop: 12 }}>
              <label className="label">Título</label>
              <input
                type="text"
                className="input"
                value={draft.rating_label}
                maxLength={200}
                onChange={(e) => update("rating_label", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Pregunta 2: resolvió + opciones */}
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-inner">
            <div className="eyebrow">Pregunta 2 — ¿Resolvió?</div>
            <div className="form-row" style={{ marginTop: 12 }}>
              <label className="label">Título</label>
              <input
                type="text"
                className="input"
                value={draft.resolvio_label}
                maxLength={200}
                onChange={(e) => update("resolvio_label", e.target.value)}
              />
            </div>

            <div className="form-row">
              <label className="label">Opciones</label>
              <p className="helper">
                ID es el código que se guarda en DB (alfanumérico + _). Marca como
                "positiva" las opciones que cuentan para el % resolvió del dashboard.
              </p>
              <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
                {draft.resolvio_options.map((o, idx) => (
                  <div key={idx} className="enc-opt-row">
                    <div className="enc-opt-fields">
                      <div>
                        <label className="enc-opt-mini-label">ID</label>
                        <input
                          type="text"
                          className="input"
                          value={o.id}
                          maxLength={32}
                          pattern="[a-z0-9_]+"
                          onChange={(e) =>
                            updateOption(idx, {
                              id: e.target.value
                                .toLowerCase()
                                .replace(/[^a-z0-9_]/g, ""),
                            })
                          }
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="enc-opt-mini-label">Etiqueta</label>
                        <input
                          type="text"
                          className="input"
                          value={o.label}
                          maxLength={100}
                          onChange={(e) => updateOption(idx, { label: e.target.value })}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="enc-opt-mini-label">Descripción</label>
                        <input
                          type="text"
                          className="input"
                          value={o.desc}
                          maxLength={200}
                          onChange={(e) => updateOption(idx, { desc: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="enc-opt-actions">
                      <label
                        className="enc-opt-positiva"
                        title="¿Cuenta como 'resolvió' positiva en métricas?"
                      >
                        <input
                          type="checkbox"
                          checked={o.positiva}
                          onChange={(e) =>
                            updateOption(idx, { positiva: e.target.checked })
                          }
                        />
                        Positiva
                      </label>
                      <button
                        type="button"
                        className="btn-outline btn-xs is-danger"
                        disabled={draft.resolvio_options.length <= 2}
                        onClick={() => removeOption(idx)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {draft.resolvio_options.length < 8 && (
                <button
                  type="button"
                  className="btn-outline btn-sm"
                  style={{ marginTop: 10 }}
                  onClick={addOption}
                >
                  + Añadir opción
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Pregunta 3: comentario */}
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-inner">
            <div className="eyebrow">Pregunta 3 — Comentario</div>
            <div className="form-row" style={{ marginTop: 12 }}>
              <label className="label">Título</label>
              <input
                type="text"
                className="input"
                value={draft.comentario_label}
                maxLength={100}
                onChange={(e) => update("comentario_label", e.target.value)}
              />
            </div>
            <div className="form-row">
              <label className="label">Placeholder</label>
              <input
                type="text"
                className="input"
                value={draft.comentario_placeholder}
                maxLength={200}
                onChange={(e) => update("comentario_placeholder", e.target.value)}
              />
            </div>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                fontSize: 14,
                color: "var(--ink-2)",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={draft.comentario_required}
                onChange={(e) => update("comentario_required", e.target.checked)}
              />
              Hacer el comentario obligatorio
            </label>
          </div>
        </div>

        <div style={{ marginTop: 22, display: "flex", gap: 10 }}>
          <button
            type="button"
            className="cta"
            style={{ maxWidth: 280 }}
            disabled={saving || !dirty}
            onClick={() => void save()}
          >
            <span className="cta-inner">
              {saving ? "Guardando…" : "Guardar cambios"}
            </span>
          </button>
          {dirty && (
            <button
              type="button"
              className="btn-outline"
              disabled={saving}
              onClick={() => setDraft(cfg)}
            >
              Descartar cambios
            </button>
          )}
        </div>

        {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
      </section>
    </Shell>
  );
}
